import { createReducer, on } from '@ngrx/store';
import { ImpersonationActions } from '@ps/auth-data-access';

import {
  ManageSurveyActions,
  ManageSurveyApiActions,
} from './manage-survey.actions';
import {
  INITIAL_MANAGE_SURVEY_STATE,
  QUOTAS_PAGE_LIMIT,
} from '../survey-state.constants';
import { ManageSurveyState } from '../survey-state.model';

export const manageSurveyReducer = createReducer(
  INITIAL_MANAGE_SURVEY_STATE,

  on(
    ManageSurveyActions.loadSurveyDetails,
    (state): ManageSurveyState => ({
      ...state,
      loading: true,
      error: null,
    }),
  ),

  on(
    ManageSurveyActions.resetSurveyDetails,
    (): ManageSurveyState => INITIAL_MANAGE_SURVEY_STATE,
  ),

  on(
    ManageSurveyApiActions.loadSurveySuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      survey,
      loading: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      survey: null,
      loading: false,
      error,
      supplierCpisLoading: false,
      supplierCpisError: null,
    }),
  ),

  on(
    ManageSurveyActions.loadSupplierCpis,
    (state): ManageSurveyState => ({
      ...state,
      supplierCpisLoading: true,
      supplierCpisError: null,
    }),
  ),

  on(
    ManageSurveyActions.loadSurveyQuotas,
    (state): ManageSurveyState => ({
      ...state,
      loading: true,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadSupplierCpisSuccess,
    (state, { supplierCpis }): ManageSurveyState => ({
      ...state,
      supplierCpis,
      supplierCpisLoading: false,
      supplierCpisError: null,
    }),
  ),

  on(
    ManageSurveyActions.loadMoreQuotas,
    (state): ManageSurveyState =>
      // Ignore while a page is already in flight so a rapid second request
      // can't advance past — and skip — the page being fetched.
      state.quotasLoadingMore
        ? state
        : {
            ...state,
            quotaLoadedPage: state.quotaLoadedPage + 1,
            quotasLoadingMore: true,
            error: null,
          },
  ),

  on(
    ManageSurveyActions.loadAllQuotas,
    (state): ManageSurveyState => ({
      ...state,
      quotasLoadingMore: true,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadSupplierCpisFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      supplierCpisLoading: false,
      supplierCpisError: error,
    }),
  ),
  on(
    ManageSurveyActions.resetSurveyQuotas,
    (state): ManageSurveyState => ({
      ...state,
      quotas: [],
      quotaTotal: 0,
      quotaLoadedPage: 0,
      quotasLoadingMore: false,
      loading: false,
      error: null,
      quotaLockingIds: [],
      quotaSavingIds: [],
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyQuotasSuccess,
    (state, { quotas, total }): ManageSurveyState => ({
      ...state,
      quotas,
      quotaTotal: total,
      quotaLoadedPage: 1,
      loading: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyQuotasFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      quotas: [],
      quotaTotal: 0,
      quotaLoadedPage: 0,
      loading: false,
      error,
    }),
  ),

  on(
    ManageSurveyApiActions.updateCurrentCpiSuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      survey,
      error: null,
      supplierCpisError: null,
      supplierCpisLoading: false,
    }),
  ),

  on(
    ManageSurveyApiActions.loadMoreQuotasSuccess,
    (state, { quotas }): ManageSurveyState => ({
      ...state,
      quotas: [...state.quotas, ...quotas],
      quotasLoadingMore: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.updateCurrentCpiFailure,
    (state): ManageSurveyState => state,
  ),

  on(
    ManageSurveyApiActions.loadMoreQuotasFailure,
    (state): ManageSurveyState => ({
      ...state,
      // Roll back the page advanced by loadMoreQuotas so a retry refetches it.
      quotaLoadedPage: state.quotaLoadedPage - 1,
      quotasLoadingMore: false,
    }),
  ),

  on(
    ManageSurveyApiActions.loadAllQuotasSuccess,
    (state, { quotas }): ManageSurveyState => ({
      ...state,
      quotas,
      // The response holds every quota, so the count is authoritative — don't
      // depend on a `total` header the unpaginated endpoint may not send.
      quotaTotal: quotas.length,
      quotaLoadedPage: Math.ceil(quotas.length / QUOTAS_PAGE_LIMIT),
      quotasLoadingMore: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadAllQuotasFailure,
    (state): ManageSurveyState => ({
      ...state,
      quotasLoadingMore: false,
    }),
  ),

  on(
    ManageSurveyActions.setQuotaLock,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaLockingIds: [...state.quotaLockingIds, psQuotaId],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaLockSuccess,
    (state, { psQuotaId, locked }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId ? { ...quota, locked } : quota,
      ),
      error: null,
      quotaLockingIds: state.quotaLockingIds.filter((id) => id !== psQuotaId),
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaLockFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaLockingIds: state.quotaLockingIds.filter((id) => id !== psQuotaId),
    }),
  ),

  on(
    ManageSurveyActions.setQuotaCurrentTarget,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaSavingIds: [...state.quotaSavingIds, psQuotaId],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCurrentTargetSuccess,
    (state, { psQuotaId, update }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId
          ? {
              ...quota,
              goal: update.goal,
              currentlyOpen: update.currentlyOpen,
              maximum: update.maximum,
              allocationPercentage: update.allocationPercentage,
              fielded: update.fielded,
              locked: update.locked,
              suppliers: update.suppliers,
            }
          : quota,
      ),
      error: null,
      quotaSavingIds: state.quotaSavingIds.filter((id) => id !== psQuotaId),
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCurrentTargetFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaSavingIds: state.quotaSavingIds.filter((id) => id !== psQuotaId),
    }),
  ),

  on(
    ManageSurveyActions.loadTrafficChannels,
    (state): ManageSurveyState => ({
      ...state,
      trafficChannelsLoading: true,
      trafficChannelsError: null,
    }),
  ),

  on(
    ManageSurveyActions.resetTrafficChannels,
    (state): ManageSurveyState => ({
      ...state,
      trafficChannels: [],
      trafficChannelsLoading: false,
      trafficChannelsError: null,
      tcStatusChangingIds: [],
    }),
  ),

  on(
    ManageSurveyApiActions.loadTrafficChannelsSuccess,
    (state, { trafficChannels }): ManageSurveyState => ({
      ...state,
      trafficChannels,
      trafficChannelsLoading: false,
      trafficChannelsError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadTrafficChannelsFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      trafficChannels: [],
      trafficChannelsLoading: false,
      trafficChannelsError: error,
    }),
  ),

  on(
    ManageSurveyActions.setTrafficChannelStatus,
    (state, { surveyId }): ManageSurveyState => ({
      ...state,
      tcStatusChangingIds: [...state.tcStatusChangingIds, surveyId],
      trafficChannelsError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setTrafficChannelStatusSuccess,
    (state, { surveyId, status }): ManageSurveyState => ({
      ...state,
      trafficChannels: state.trafficChannels.map((channel) =>
        channel.id === surveyId ? { ...channel, status } : channel,
      ),
      trafficChannelsError: null,
      tcStatusChangingIds: state.tcStatusChangingIds.filter(
        (id) => id !== surveyId,
      ),
    }),
  ),

  on(
    ManageSurveyApiActions.setTrafficChannelStatusFailure,
    (state, { surveyId }): ManageSurveyState => ({
      ...state,
      tcStatusChangingIds: state.tcStatusChangingIds.filter(
        (id) => id !== surveyId,
      ),
    }),
  ),

  on(
    ImpersonationActions.contextSettled,
    (): ManageSurveyState => INITIAL_MANAGE_SURVEY_STATE,
  ),
);
